import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { callAPIWithoutAuth, callAPIWithAuth } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';
import { getGuestCart } from '../common/guestCart';

export const signup = createAsyncThunk(
  'auth/signup',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await callAPIWithoutAuth(
        apiUrls?.signup,
        {},
        "POST",
        {
          full_name: userData.name,
          email: userData.email,
          password: userData.password,
          phone: userData.phone
        }
      );

      if (response?.data?.success) {
        return {
          message: response.data.message || 'Account created successfully!',
          data: response.data.data
        };
      } else {
        return rejectWithValue(response?.data?.message || 'Signup failed. Please try again.');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Signup failed. Please try again.');
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const guestCart = getGuestCart();
      const guestCartItems = guestCart.items || [];
      const loginPayload = {
        email: email,
        password: password,
        guest_cart: guestCartItems.map(item => ({
          product_id: item.productId,
          quantity: item.quantity,
          tire_detail_id: item.tire_detail_id || null,
          battery_detail_id: item.battery_detail_id || null,
          windshield_detail_id: item.windshield_detail_id || null,
          product_variant_id: item.product_variant_id || null
        }))
      };
      const response = await callAPIWithoutAuth(
        apiUrls?.login,
        {},
        "POST",
        loginPayload
      );

      if (response?.data?.success) {
        if (response.data.data?.token) {
          localStorage.removeItem('guest_cart')
          localStorage.setItem('accessToken', response.data.data.token);
        }
        if (response.data.data?.user) {
          localStorage.setItem('userData', JSON.stringify(response.data.data.user));

        }

        return {
          user: response.data.data?.user,
          token: response.data.data?.token,
          message: response.data.message || 'Login successful!'
        };
      } else {
        return rejectWithValue(response?.data?.message || 'Login failed');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
)

export const forgotPasswordSendMail = createAsyncThunk(
  'auth/forgotPasswordSendMail',
  async (email, { rejectWithValue }) => {
    try {
      const response = await callAPIWithoutAuth(
        apiUrls?.forgotPasswordSendMail, {}, 'POST',
        { email }

      );

      if (response?.data?.success) {
        return {
          success: true,
          message: response.data.message || 'OTP sent successfully!'
        };
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to send OTP');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send OTP');
    }
  }
);

export const forgotPasswordVerifyOTP = createAsyncThunk(
  'auth/forgotPasswordVerifyOTP',
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await callAPIWithoutAuth(
        apiUrls?.forgotPasswordVerifyOTP, {}, 'POST',
        { email, otp },

      );

      if (response?.data?.success) {
        return {
          success: true,
          message: response.data.message || 'OTP verified successfully!'
        };
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to verify OTP');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to verify OTP');
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ email, password, password_confirmation }, { rejectWithValue }) => {
    try {
      const response = await callAPIWithoutAuth(
        apiUrls?.resetPassword, {}, 'POST',
        { email, password, password_confirmation },

      );

      if (response?.data?.success) {
        return {
          success: true,
          message: response.data.message || 'Password reset successfully!'
        };
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to reset password');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reset password');
    }
  }
);

export const socialLogin = createAsyncThunk(
  'auth/socialLogin',
  async (provider, { rejectWithValue }) => {
    try {
      return rejectWithValue('Social login not implemented yet');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || `${provider} login failed`);
    }
  }
);

export const googleLoginS = createAsyncThunk(
  'auth/googleLogin',
  async ({ token, guest_cart = [] }, { rejectWithValue }) => {
    try {
      const guestCart = guest_cart.length ? guest_cart : JSON.parse(localStorage.getItem('guest_cart') || '{"items":[]}').items;

      const formattedGuestCart = guestCart.map(item => ({
        product_id: item.productId,
        quantity: item.quantity,
        tire_detail_id: item.tire_detail_id || null,
        battery_detail_id: item.battery_detail_id || null,
        windshield_detail_id: item.windshield_detail_id || null,
        product_variant_id: item.product_variant_id || null
      }));

      const response = await callAPIWithoutAuth(
        apiUrls?.authGoogle,
        {},
        "POST",
        {
          token: token,
          guest_cart: formattedGuestCart
        }
      );

      if (response?.data?.success) {
        if (response.data.data?.token) {
          localStorage.removeItem('guest_cart');
          localStorage.setItem('accessToken', response.data.data.token);
        }
        if (response.data.data?.user) {
          localStorage.setItem('userData', JSON.stringify(response.data.data.user));
        }

        return {
          user: response.data.data?.user,
          token: response.data.data?.token,
          message: response.data.message || 'Google login successful!'
        };
      } else {
        return rejectWithValue(response?.data?.message);
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message);
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('refreshToken');
      sessionStorage.removeItem('user');

      return { success: true, message: 'Logged out successfully' };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Logout failed');
    }
  }
);

export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const userStr = localStorage.getItem('userData');

      if (!token || !userStr) {
        return rejectWithValue('No active session');
      }

      const user = JSON.parse(userStr);

      return {
        user,
        token,
        isAuthenticated: true
      };
    } catch (error) {
      return rejectWithValue('No active session');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: JSON.parse(localStorage.getItem("userData") || null),
    token: localStorage.getItem('accessToken') || null,
    isAuthenticated: !!localStorage.getItem('accessToken'),
    refreshToken: null,
    loading: false,
    signupLoading: false,
    loginLoading: false,
    socialLoading: false,
    logoutLoading: false,
    forgotPasswordLoading: false,
    forgotPasswordVerifyLoading: false,
    resetPasswordLoading: false,
    updatingProfile: false,
    updatingAddress: false,
    updatingPassword: false,
    updateError: null,
    updateProfileSuccess: false,
    updateAddressSuccess: false,
    updatePasswordSuccess: false,
    otpSent: false,
    otpVerified: false,
    currentStep: 1,
    loginForm: {
      email: '',
      password: '',
      rememberMe: false
    },
    signupForm: {
      name: '',
      phone: '',
      email: '',
      password: '',
      location: '',
      agreeToTerms: false
    },
    isLoginModalOpen: false,
    showPassword: false,
    showSignupPassword: false,
    error: null,
    errors: {},
    loginSuccess: false,
    signupSuccess: false,
    forgotPasswordSuccess: false,
    resetPasswordSuccess: false,
    message: {
      type: '',
      text: ''
    }
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.updatingProfile = false;
      state.updatingPassword = false;
      state.updateError = null;
      state.updateProfileSuccess = false;
      state.updateAddressSuccess = false;
      state.updatePasswordSuccess = false;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('userData');
    },
    setAuthStep: (state, action) => {
      state.currentStep = action.payload;
    },
    clearError: (state) => {
      state.error = null;
      state.updateError = null;
    },
    setToken: (state, action) => {
      state.token = action.payload;
      state.isAuthenticated = !!action.payload;
      localStorage.setItem('accessToken', action.payload);
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      localStorage.setItem('userData', JSON.stringify(action.payload));
    },
    clearUpdateStatus: (state) => {
      state.updateProfileSuccess = false;
      state.updateAddressSuccess = false;
      state.updatePasswordSuccess = false;
      state.updateError = null;
    },
    clearProfileUpdateStatus: (state) => {
      state.updateProfileSuccess = false;
    },
    clearAddressUpdateStatus: (state) => {
      state.updateAddressSuccess = false;
    },
    clearPasswordUpdateStatus: (state) => {
      state.updatePasswordSuccess = false;
    },
    toggleEditMode: (state, action) => {
      const { section, isEditing } = action.payload;
      state[`editing${section}`] = isEditing;
    },
    setLoginFormField: (state, action) => {
      const { name, value } = action.payload;
      if (state.loginForm) {
        state.loginForm[name] = value;
      }
      if (state.errors[name]) {
        delete state.errors[name];
      }
    },
    resetLoginForm: (state) => {
      state.loginForm = {
        email: '',
        password: '',
        rememberMe: false
      };
      state.showPassword = false;
      state.errors = {};
    },
    setSignupFormField: (state, action) => {
      const { name, value } = action.payload;
      if (state.signupForm) {
        state.signupForm[name] = value;
      }
      if (state.errors[name]) {
        delete state.errors[name];
      }
    },
    resetSignupForm: (state) => {
      state.signupForm = {
        name: '',
        phone: '',
        email: '',
        password: '',
        location: '',
        agreeToTerms: false
      };
      state.showSignupPassword = false;
      state.errors = {};
    },
    openLoginModal: (state) => {
      state.isLoginModalOpen = true;
    },
    closeLoginModal: (state) => {
      state.isLoginModalOpen = false;
      state.errors = {};
      state.error = null;
      state.message = { type: '', text: '' };
    },
    togglePassword: (state) => {
      state.showPassword = !state.showPassword;
    },
    toggleSignupPassword: (state) => {
      state.showSignupPassword = !state.showSignupPassword;
    },
    setShowPassword: (state, action) => {
      state.showPassword = action.payload;
    },
    setErrors: (state, action) => {
      state.errors = action.payload;
    },
    clearErrors: (state) => {
      state.errors = {};
      state.error = null;
    },
    setMessage: (state, action) => {
      state.message = {
        type: action.payload.type,
        text: action.payload.text
      };
    },
    clearMessage: (state) => {
      state.message = {
        type: '',
        text: ''
      };
    },
    clearAuthError: (state) => {
      state.error = null;
    },
    clearLoginSuccess: (state) => {
      state.loginSuccess = false;
    },
    clearSignupSuccess: (state) => {
      state.signupSuccess = false;
    },
    clearForgotPasswordSuccess: (state) => {
      state.forgotPasswordSuccess = false;
    },
    clearResetPasswordSuccess: (state) => {
      state.resetPasswordSuccess = false;
    },
    resetAuthState: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.loginForm = {
        email: '',
        password: '',
        rememberMe: false
      };
      state.signupForm = {
        name: '',
        phone: '',
        email: '',
        password: '',
        location: '',
        agreeToTerms: false
      };
      state.isLoginModalOpen = false;
      state.showPassword = false;
      state.showSignupPassword = false;
      state.errors = {};
      state.error = null;
      state.message = { type: '', text: '' };
      state.currentStep = 1;
      state.otpSent = false;
      state.otpVerified = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(signup.pending, (state) => {
        state.loading = true;
        state.signupLoading = true;
        state.error = null;
        state.signupSuccess = false;
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.loading = false;
        state.signupLoading = false;
        state.signupSuccess = true;
        state.signupForm = {
          name: '',
          phone: '',
          email: '',
          password: '',
          location: '',
          agreeToTerms: false
        };
        state.showSignupPassword = false;
        state.errors = {};
        state.message = {
          type: 'success',
          text: action.payload.message || 'Account created successfully!'
        };
      })
      .addCase(signup.rejected, (state, action) => {
        state.loading = false;
        state.signupLoading = false;
        state.signupSuccess = false;
        state.error = action.payload || 'Signup failed. Please try again.';
        state.message = {
          type: 'error',
          text: action.payload || 'Signup failed. Please try again.'
        };
      })

      .addCase(login.pending, (state) => {
        state.loading = true;
        state.loginLoading = true;
        state.error = null;
        state.loginSuccess = false;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.loginLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.loginSuccess = true;
        state.loginForm = {
          email: '',
          password: '',
          rememberMe: false
        };
        state.showPassword = false;
        state.errors = {};
        state.message = {
          type: 'success',
          text: action.payload.message || 'Login successful!'
        };
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.loginLoading = false;
        state.loginSuccess = false;
        state.error = action.payload || 'Login failed';
        state.message = {
          type: 'error',
          text: action.payload || 'Login failed. Please try again.'
        };
      })

      .addCase(socialLogin.pending, (state) => {
        state.loading = true;
        state.socialLoading = true;
        state.error = null;
      })
      .addCase(socialLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.socialLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.loginSuccess = true;
        state.message = {
          type: 'success',
          text: action.payload.message || `Successfully signed in with ${action.payload.provider}!`
        };
      })
      .addCase(socialLogin.rejected, (state, action) => {
        state.loading = false;
        state.socialLoading = false;
        state.error = action.payload || 'Social login failed';
        state.message = {
          type: 'error',
          text: action.payload || 'Social login failed. Please try again.'
        };
      })

      .addCase(logout.pending, (state) => {
        state.logoutLoading = true;
      })
      .addCase(logout.fulfilled, (state, action) => {
        state.logoutLoading = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.message = {
          type: 'success',
          text: action.payload?.message || 'Logged out successfully'
        };
      })
      .addCase(logout.rejected, (state, action) => {
        state.logoutLoading = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.message = {
          type: 'success',
          text: 'Logged out successfully'
        };
      })

      .addCase(checkAuth.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
      })
      .addCase(checkAuth.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      })

      .addCase(forgotPasswordSendMail.pending, (state) => {
        state.forgotPasswordLoading = true;
        state.error = null;
        state.forgotPasswordSuccess = false;
      })
      .addCase(forgotPasswordSendMail.fulfilled, (state, action) => {
        state.forgotPasswordLoading = false;
        state.forgotPasswordSuccess = true;
        state.message = {
          type: 'success',
          text: action.payload.message
        };
      })
      .addCase(forgotPasswordSendMail.rejected, (state, action) => {
        state.forgotPasswordLoading = false;
        state.forgotPasswordSuccess = false;
        state.error = action.payload;
        state.message = {
          type: 'error',
          text: action.payload || 'Failed to send OTP'
        };
      })

      .addCase(forgotPasswordVerifyOTP.pending, (state) => {
        state.forgotPasswordVerifyLoading = true;
        state.error = null;
      })
      .addCase(forgotPasswordVerifyOTP.fulfilled, (state, action) => {
        state.forgotPasswordVerifyLoading = false;
        state.otpVerified = true;
        state.message = {
          type: 'success',
          text: action.payload.message
        };
      })
      .addCase(forgotPasswordVerifyOTP.rejected, (state, action) => {
        state.forgotPasswordVerifyLoading = false;
        state.otpVerified = false;
        state.error = action.payload;
        state.message = {
          type: 'error',
          text: action.payload || 'Failed to verify OTP'
        };
      })

      .addCase(googleLoginS.pending, (state) => {
        state.loading = true;
        state.socialLoading = true;
        state.error = null;
      })
      .addCase(googleLoginS.fulfilled, (state, action) => {
        state.loading = false;
        state.socialLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.loginSuccess = true;
        state.message = {
          type: 'success',
          text: action.payload.message || 'Google login successful!'
        };
      })
      .addCase(googleLoginS.rejected, (state, action) => {
        state.loading = false;
        state.socialLoading = false;
        state.loginSuccess = false;
        state.error = action.payload || 'Google login failed';
        state.message = {
          type: 'error',
          text: action.payload || 'Google login failed. Please try again.'
        };
      })

      .addCase(resetPassword.pending, (state) => {
        state.resetPasswordLoading = true;
        state.error = null;
        state.resetPasswordSuccess = false;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.resetPasswordLoading = false;
        state.resetPasswordSuccess = true;
        state.message = {
          type: 'success',
          text: action.payload.message
        };
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.resetPasswordLoading = false;
        state.resetPasswordSuccess = false;
        state.error = action.payload;
        state.message = {
          type: 'error',
          text: action.payload || 'Failed to reset password'
        };
      })
  }
});





export const {
  setLoginFormField,
  resetLoginForm,
  setSignupFormField,
  resetSignupForm,
  openLoginModal,
  closeLoginModal,
  togglePassword,
  toggleSignupPassword,
  setShowPassword,
  setErrors,
  clearErrors,
  setMessage,
  clearMessage,
  clearAuthError,
  clearLoginSuccess,
  clearSignupSuccess,
  clearForgotSuccess,
  clearResetSuccess,
  resetAuthState,
  setAuthStep,
  clearError,
  setToken,
  setUser,
  clearUpdateStatus,
  clearProfileUpdateStatus,
  clearAddressUpdateStatus,
  clearPasswordUpdateStatus,
  toggleEditMode
} = authSlice.actions;

export default authSlice.reducer;